import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { Markdown } from "tiptap-markdown";
import { CommentMark } from "./CommentMark";
import CommentsPanel, { ContentComment as ContentCommentType } from "./CommentsPanel";
import {
  AddCommentOutlined,
  ChatBubbleOutline,
  Close,
  ContentCopy,
  Edit,
  EditNoteOutlined,
  FilterAlt,
  Lightbulb,
  PictureAsPdf,
} from "@mui/icons-material";
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import LoadingButton from "../../components/LoadingButton";
import { countWords, hostname, parseError } from "../../utils";
import ContentTypeChip from "./ContentTypeChip";
import ContentViewerImageSlider from "./ContentViewerImageSlider";

export default ({
  open,
  onClose,
  content,
  eventListener,
  downloadPdf,
  getToken,
  companyId,
  currentUserUid,
}: {
  open: boolean;
  onClose: (updatedContent?: any) => void;
  content: any;
  eventListener?: Function;
  downloadPdf?: (id: string) => void;
  getToken?: Function;
  companyId?: string;
  currentUserUid?: string;
}) => {
  const [title, setTitle] = useState("");
  const [markdown, setMarkdown] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectingHook, setSelectingHook] = useState(false);
  const [selectedHook, setSelectedHook] = useState<string | null>(null);
  const [generatingContent, setGeneratingContent] = useState(false);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  type RemotePresence = {
    userId: string;
    clientId: string;
    name: string;
    avatarUrl: string | null;
    color: string;
    cursor: { pos: number; end: number };
  };
  const [remotePresence, setRemotePresence] = useState<RemotePresence[]>([]);
  const [selfPresence, setSelfPresence] = useState<{ userId: string; color: string } | null>(null);
  const [cursorTick, setCursorTick] = useState(0);
  const [selectionTick, setSelectionTick] = useState(0);

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestRef = useRef<{ title: string; markdown: string }>({ title: "", markdown: "" });
  const suppressUpdateRef = useRef(false);

  const [comments, setComments] = useState<ContentCommentType[]>([]);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [pendingAnchor, setPendingAnchor] = useState<
    { from: number; to: number; quotedText: string } | null
  >(null);
  const [showResolvedComments, setShowResolvedComments] = useState(false);

  const loadedFromJsonRef = useRef(false);
  const collabActiveRef = useRef(false);
  const collabVersionRef = useRef(0);
  const collabClientIdRef = useRef<string | null>(null);
  const collabWsRef = useRef<WebSocket | null>(null);
  const collabSendIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const collabDirtyRef = useRef(false);
  const editorBoxRef = useRef<HTMLDivElement | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false, autolink: true }),
      Image,
      Markdown.configure({ html: false, breaks: true, transformPastedText: true }),
      CommentMark.configure({
        onCommentClick: (commentId: string) => {
          setActiveThreadId(commentId);
          setCommentsOpen(true);
        },
      }),
    ],
    content: "",
    onUpdate: ({ editor: ed }) => {
      if (suppressUpdateRef.current) return;
      const md = (ed.storage as any).markdown.getMarkdown();
      setMarkdown(md);
      latestRef.current.markdown = md;
      if (collabActiveRef.current) {
        collabDirtyRef.current = true;
      } else {
        scheduleAutosave();
      }
    },
    onTransaction: ({ transaction }) => {
      if (suppressUpdateRef.current) return;
      if (!collabActiveRef.current) return;
      if (!transaction.docChanged) return;
      const mapping = transaction.mapping;
      setRemotePresence((prev) =>
        prev.map((p) => ({
          ...p,
          cursor: {
            pos: mapping.map(p.cursor.pos),
            end: mapping.map(p.cursor.end),
          },
        }))
      );
    },
    onSelectionUpdate: () => {
      if (collabActiveRef.current) setCursorTick((t) => t + 1);
      setSelectionTick((t) => t + 1);
    },
  });

  const performSave = useCallback(async () => {
    if (!getToken || !companyId || !content?.id) return;
    const { title: t, markdown: m } = latestRef.current;
    if (!t || !m) return;
    setSaveState("saving");
    try {
      const token = await getToken();
      const editorJson = editor ? editor.getJSON() : null;
      const updatedContent = {
        ...content,
        title: t,
        variants: {
          ...(content.variants || {}),
          default: m,
          ...(editorJson ? { editorJson } : {}),
        },
      };
      await axios.post(
        `${hostname}/content/${content.id}`,
        { companyId, content: updatedContent },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      setSaveState("saved");
    } catch (err) {
      console.error("Autosave failed:", err);
      setSaveState("error");
    }
  }, [getToken, companyId, content, editor]);

  const scheduleAutosave = useCallback(() => {
    if (collabActiveRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveState("saving");
    saveTimerRef.current = setTimeout(() => {
      performSave();
    }, 1200);
  }, [performSave]);

  const commentsRef = useRef<ContentCommentType[]>([]);
  useEffect(() => {
    commentsRef.current = comments;
  }, [comments]);

  const fetchCommentsRef = useRef<() => Promise<void>>();

  const reapplyCommentMarks = useCallback(
    (commentList: ContentCommentType[]) => {
      if (!editor) return;
      const tr = editor.state.tr;
      const markType = editor.schema.marks.commentMark;
      if (!markType) return;
      // Clear existing comment marks, then re-add from the source-of-truth list
      tr.removeMark(0, editor.state.doc.content.size, markType);

      const doc = tr.doc;
      const docSize = doc.content.size;

      // Build a flat text representation of the doc and a map from each index
      // in that string back to a ProseMirror document position. We use the
      // same single-space block separator that startNewCommentFromSelection
      // uses when producing quotedText, so selections that span paragraphs
      // can be matched with one indexOf on the flat string.
      let flatText = "";
      const flatToDocPos: number[] = [];
      doc.descendants((node, pos) => {
        if (node.isText) {
          const text = node.text || "";
          for (let i = 0; i < text.length; i++) {
            flatToDocPos.push(pos + i);
          }
          flatText += text;
          return false;
        }
        if (node.isTextblock) {
          // After this block's text, emit a single-space separator to mirror
          // textBetween's blockSeparator=" ". Map that separator char to the
          // position immediately after the block's closing token.
          return true;
        }
        return true;
      });
      // Inject separators between consecutive textblocks by walking again —
      // doc.descendants above handled text content, but to insert separators
      // at block boundaries we rely on textBetween's own output instead for
      // the string, and map positions incrementally.
      // Replace the assembled flatText/flatToDocPos with a position-aware
      // walk using textBetween semantics:
      flatText = "";
      flatToDocPos.length = 0;
      let lastBlockEnd = -1;
      doc.descendants((node, pos) => {
        if (node.isText) {
          const text = node.text || "";
          for (let i = 0; i < text.length; i++) {
            flatToDocPos.push(pos + i);
          }
          flatText += text;
          return false;
        }
        if (node.isTextblock) {
          // If we already emitted a previous textblock, inject a space to
          // match textBetween's block separator behavior.
          if (lastBlockEnd >= 0) {
            flatText += " ";
            flatToDocPos.push(lastBlockEnd);
          }
          lastBlockEnd = pos + node.nodeSize;
          return true;
        }
        return true;
      });

      const positionsMatch = (from: number, to: number, quoted: string) => {
        if (from < 0 || to <= from || to > docSize) return false;
        const actual = doc.textBetween(from, to, " ");
        return actual === quoted;
      };

      const findInFlat = (quoted: string): { from: number; to: number } | null => {
        if (!quoted) return null;
        const idx = flatText.indexOf(quoted);
        if (idx < 0) return null;
        const startDocPos = flatToDocPos[idx];
        // The end position is one past the last matched character; if the
        // final matched index is a real text char, its docPos + 1 is the
        // end of that char in the doc. If the final matched char is an
        // injected separator (between blocks), we use the recorded block-end
        // position directly.
        const lastFlatIdx = idx + quoted.length - 1;
        const lastDocPos = flatToDocPos[lastFlatIdx];
        const nextFlatIdx = idx + quoted.length;
        const endDocPos =
          nextFlatIdx < flatToDocPos.length
            ? flatToDocPos[nextFlatIdx]
            : lastDocPos + 1;
        if (startDocPos === undefined || endDocPos === undefined) return null;
        return { from: startDocPos, to: endDocPos };
      };

      // Only top-level (root) threads get anchored marks
      const roots = commentList.filter((c) => !c.parentCommentId);
      for (const root of roots) {
        if (!root.quotedText) continue;
        if (root.resolved && !showResolvedComments) continue;

        let found: { from: number; to: number } | null = null;

        // 1) Try the stored positions first — exact and multi-block safe.
        if (
          typeof root.anchorFrom === "number" &&
          typeof root.anchorTo === "number" &&
          positionsMatch(root.anchorFrom, root.anchorTo, root.quotedText)
        ) {
          found = { from: root.anchorFrom, to: root.anchorTo };
        }

        // 2) Fall back to a text search across the whole doc (handles
        //    selections that span paragraphs because we include a block
        //    separator in the flat string).
        if (!found) {
          found = findInFlat(root.quotedText);
        }

        if (found) {
          tr.addMark(
            found.from,
            found.to,
            markType.create({ commentId: root.id, resolved: root.resolved })
          );
        }
      }
      suppressUpdateRef.current = true;
      editor.view.dispatch(tr);
      suppressUpdateRef.current = false;
    },
    [editor, showResolvedComments]
  );

  const forceApplyRemoteContent = useCallback(
    (remoteJson: any | null, remoteMd: string, remoteTitle: string) => {
      if (!editor) return;
      const sel = editor.state.selection;
      const anchor = sel.from;
      suppressUpdateRef.current = true;
      // Prefer editorJson (preserves comment marks & structure) over markdown
      editor.commands.setContent(remoteJson || remoteMd || "");
      suppressUpdateRef.current = false;
      const size = editor.state.doc.content.size;
      const clamped = Math.min(anchor, size);
      try {
        editor.commands.setTextSelection(clamped);
      } catch (_) {}
      latestRef.current.markdown = remoteMd;
      setMarkdown(remoteMd);
      if (remoteTitle !== latestRef.current.title) {
        latestRef.current.title = remoteTitle;
        setTitle(remoteTitle);
      }
      // Always reapply comment marks from the source-of-truth list so they
      // anchor to the correct text positions after content changes.
      reapplyCommentMarks(commentsRef.current);
    },
    [editor, reapplyCommentMarks]
  );

  // Send pending edits over WebSocket (called on an interval while collab is active)
  const collabFlush = useCallback(() => {
    if (!collabActiveRef.current || !editor) return;
    const ws = collabWsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;

    if (collabDirtyRef.current) {
      collabDirtyRef.current = false;
      const sel = editor.state.selection;
      ws.send(JSON.stringify({
        type: "edit",
        markdown: latestRef.current.markdown,
        editorJson: editor.getJSON(),
        title: latestRef.current.title,
        cursor: { pos: sel.from, end: sel.to },
      }));
    } else {
      // Still send cursor updates even when not editing
      const sel = editor.state.selection;
      ws.send(JSON.stringify({
        type: "cursor",
        cursor: { pos: sel.from, end: sel.to },
      }));
    }
  }, [editor]);

  const joinCollab = useCallback(async () => {
    if (!editor || !getToken || !companyId || !content?.id) return;
    if (collabActiveRef.current) return;

    try {
      const token = await getToken();
      // Build WebSocket URL from the HTTP hostname
      const wsProtocol = hostname.startsWith("https") ? "wss" : "ws";
      const wsHost = hostname.replace(/^https?:\/\//, "");
      const wsUrl = `${wsProtocol}://${wsHost}/content/${content.id}/collab/ws?token=${encodeURIComponent(token)}&companyId=${encodeURIComponent(companyId)}`;

      const ws = new WebSocket(wsUrl);
      collabWsRef.current = ws;

      ws.onopen = () => {
        collabActiveRef.current = true;
        collabDirtyRef.current = false;
        // Start send interval to flush edits every ~1s
        if (collabSendIntervalRef.current) clearInterval(collabSendIntervalRef.current);
        collabSendIntervalRef.current = setInterval(collabFlush, 1000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === "init") {
            collabVersionRef.current = data.version;
            collabClientIdRef.current = data.youAre?.clientId || null;
            if (data.youAre) {
              setSelfPresence({ userId: data.youAre.userId, color: data.youAre.color });
            }
            forceApplyRemoteContent(data.editorJson || null, data.markdown, data.title);
            const others = (data.presence || []).filter(
              (p: RemotePresence) => p.clientId !== collabClientIdRef.current
            );
            setRemotePresence(others);
          } else if (data.type === "update") {
            // Remote edit from another client — apply it
            collabVersionRef.current = data.version;
            forceApplyRemoteContent(data.editorJson || null, data.markdown, data.title);
            const others = (data.presence || []).filter(
              (p: RemotePresence) => p.clientId !== collabClientIdRef.current
            );
            setRemotePresence(others);
          } else if (data.type === "presence") {
            const others = (data.presence || []).filter(
              (p: RemotePresence) => p.clientId !== collabClientIdRef.current
            );
            setRemotePresence(others);
          } else if (data.type === "ack") {
            collabVersionRef.current = data.version;
          } else if (data.type === "comments_changed") {
            fetchCommentsRef.current?.();
          } else if (data.type === "error") {
            console.error("Collab WS error:", data.error);
          }
        } catch (err) {
          console.error("Failed to parse collab message:", err);
        }
      };

      ws.onclose = () => {
        collabActiveRef.current = false;
        if (collabSendIntervalRef.current) {
          clearInterval(collabSendIntervalRef.current);
          collabSendIntervalRef.current = null;
        }
        collabWsRef.current = null;
        setRemotePresence([]);
        setSelfPresence(null);
      };

      ws.onerror = (err) => {
        console.error("Collab WebSocket error:", err);
      };
    } catch (err) {
      console.error("Collab join failed:", err);
    }
  }, [editor, getToken, companyId, content, forceApplyRemoteContent, collabFlush]);

  const leaveCollab = useCallback(() => {
    if (!collabActiveRef.current && !collabWsRef.current) return;
    collabActiveRef.current = false;
    if (collabSendIntervalRef.current) {
      clearInterval(collabSendIntervalRef.current);
      collabSendIntervalRef.current = null;
    }
    // Send any final pending edits before closing
    const ws = collabWsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      if (collabDirtyRef.current && editor) {
        collabDirtyRef.current = false;
        ws.send(JSON.stringify({
          type: "edit",
          markdown: latestRef.current.markdown,
          editorJson: editor.getJSON(),
          title: latestRef.current.title,
          cursor: { pos: 0, end: 0 },
        }));
      }
      ws.close();
    }
    collabWsRef.current = null;
    setRemotePresence([]);
    setSelfPresence(null);
    collabClientIdRef.current = null;
    collabVersionRef.current = 0;
    collabDirtyRef.current = false;
  }, [editor]);

  const isIdeated = content && content.status === "ideated";
  const isGenerating = content && content.status === "generating";

  const validForm = () => {
    return title && markdown;
  };

  const copyContent = async (text: string) => {
    if (!eventListener) return;

    try {
      await navigator.clipboard.writeText(text);
      eventListener({
        type: "SET_INFO",
        info: "Content copied to clipboard",
      });
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: "Error copying to clipboard",
      });
    }
  };

  const handleSubmit = async () => {
    if (!validForm()) return;

    setLoading(true);
    try {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      if (collabActiveRef.current) {
        collabFlush();
      } else {
        await performSave();
      }
      const editorJson = editor ? editor.getJSON() : null;
      const updatedContent = {
        ...content,
        title: title,
        variants: {
          ...content.variants,
          default: markdown,
          ...(editorJson ? { editorJson } : {}),
        },
      };
      if (onClose) {
        onClose(updatedContent);
      }
    } catch (error) {
      console.error("Error saving content:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
      performSave();
    }
    setTitle("");
    setMarkdown("");
    onClose();
  };

  // Function to handle selecting a hook and generating content
  const selectHookAndGenerate = async (hook: string) => {
    if (!getToken || !companyId || !content || !eventListener) {
      console.error("Missing required props for hook selection");
      return;
    }

    setSelectedHook(hook);
    setGeneratingContent(true);

    try {
      const token = await getToken();

      // Call the API to select the hook and generate content
      const response = await axios.post(
        `${hostname}/content/${content.id}/select-hook`,
        {
          companyId,
          selectedHook: hook,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Show success message
      eventListener({
        type: "SET_SUCCESS",
        info: "Content generated successfully!",
      });

      // Close this dialog first
      onClose();

      // If we got updated content back, reopen the editor with the new draft
      if (response.data.success && response.data.content) {
        // Wait a moment for the dialog to close before reopening
        setTimeout(() => {
          onClose(response.data.content);
        }, 100);
      }
    } catch (error) {
      console.error("Error selecting hook:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setGeneratingContent(false);
      setSelectingHook(false);
    }
  };

  useEffect(() => {
    if (!content) return;
    if (content.status === "ideated" || content.status === "generating") {
      setSelectingHook(true);
      const t = content.title || "Select a Hook for Your Content";
      setTitle(t);
      setMarkdown(content.topicSummary || "");
      latestRef.current = { title: t, markdown: content.topicSummary || "" };
      if (content.selectedHook) setSelectedHook(content.selectedHook);
    } else {
      const t = content.title || "";
      const md = content.variants && content.variants.default ? content.variants.default : "";
      const savedJson = content.variants?.editorJson || null;
      loadedFromJsonRef.current = !!savedJson;
      setTitle(t);
      setMarkdown(md);
      latestRef.current = { title: t, markdown: md };
      if (editor) {
        suppressUpdateRef.current = true;
        // Prefer editorJson (preserves comment marks & structure) over markdown
        editor.commands.setContent(savedJson || md || "");
        suppressUpdateRef.current = false;
      }
      setSaveState("idle");
    }
  }, [open, content, editor]);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (collabSendIntervalRef.current) clearInterval(collabSendIntervalRef.current);
      if (collabWsRef.current) {
        collabWsRef.current.close();
        collabWsRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!open || !editor || !content) return;
    if (content.status === "ideated" || content.status === "generating") return;
    joinCollab();
    return () => {
      leaveCollab();
    };
  }, [open, editor, content?.id, joinCollab, leaveCollab]);

  const fetchComments = useCallback(async () => {
    if (!content?.id || !getToken || !companyId) return;
    try {
      const token = await getToken();
      const res = await axios.get(
        `${hostname}/content/${content.id}/comments?companyId=${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setComments(res.data.comments || []);
    } catch (err) {
      console.error("Failed to load comments:", err);
    }
  }, [content?.id, getToken, companyId]);
  fetchCommentsRef.current = fetchComments;

  // Fetch comments when the editor opens for a content item
  useEffect(() => {
    if (!open || !content?.id || !getToken || !companyId) return;
    if (content.status === "ideated" || content.status === "generating") return;
    fetchComments();
  }, [open, content?.id, companyId, getToken, fetchComments]);

  // Reapply comment marks whenever comments change or editor content changes.
  useEffect(() => {
    if (!editor) return;
    reapplyCommentMarks(comments);
  }, [comments, editor, reapplyCommentMarks]);

  // Click handler: when user clicks a highlighted comment span, open that thread
  useEffect(() => {
    const el = editorBoxRef.current;
    if (!el) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const span = target.closest("[data-comment-id]") as HTMLElement | null;
      if (span) {
        const id = span.getAttribute("data-comment-id");
        if (id) {
          setActiveThreadId(id);
          setCommentsOpen(true);
        }
      }
    };
    el.addEventListener("click", handler);
    return () => el.removeEventListener("click", handler);
  }, [open]);

  const startNewCommentFromSelection = useCallback(() => {
    if (!editor) return;
    const { from, to } = editor.state.selection;
    if (from === to) return;
    const quoted = editor.state.doc.textBetween(from, to, " ");
    if (!quoted.trim()) return;
    setPendingAnchor({ from, to, quotedText: quoted });
    setCommentsOpen(true);
  }, [editor]);

  const hasTextSelection = useMemo(() => {
    if (!editor) return false;
    const { from, to } = editor.state.selection;
    return from !== to;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, selectionTick]);

  if (!content) return <></>;
  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      fullScreen
    >
      <DialogTitle>
        <Box>
          {/* Title Row */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                minHeight: 32,
              }}
            >
              {content.campaignColor && (
                <Box
                  sx={{
                    backgroundColor: content.campaignColor,
                    minWidth: 7,
                    width: 7,
                    height: 32,
                    borderRadius: 3,
                    marginRight: 1,
                    flexShrink: 0,
                  }}
                />
              )}
              <Typography variant={"h6"}>{content.title}</Typography>
            </Box>
            <IconButton onClick={() => onClose()}>
              <Close />
            </IconButton>
          </Box>

          {/* Chips Row */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {/* Content Type Badge */}
            {content.type && (
              <ContentTypeChip type={content.type} size="small" />
            )}

            {/* Funnel Stage Badge */}
            {content.funnelStage && content.funnelStage !== "N/A" && (
              <Chip
                icon={<FilterAlt fontSize="small" />}
                label={content.funnelStage.toUpperCase()}
                size="small"
                color={
                  content.funnelStage === "tofu"
                    ? "success"
                    : content.funnelStage === "mofu"
                    ? "info"
                    : content.funnelStage === "bofu"
                    ? "secondary"
                    : "default"
                }
                sx={{ fontWeight: "bold" }}
              />
            )}
          </Box>
        </Box>
      </DialogTitle>

      {(!isIdeated && !isGenerating) || !selectingHook ? (
        // Regular content editor header
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            px: 3,
            py: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h6"
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Edit sx={{ mr: 1 }} fontSize="small" />
            Content Editor
          </Typography>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {(remotePresence.length > 0 || selfPresence) && (
              <AvatarGroup max={5} sx={{ mr: 1 }}>
                {selfPresence && (
                  <Tooltip title="You">
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        fontSize: 12,
                        bgcolor: selfPresence.color,
                        border: "2px solid white",
                      }}
                    >
                      {"You".charAt(0)}
                    </Avatar>
                  </Tooltip>
                )}
                {remotePresence.map((p) => (
                  <Tooltip key={p.clientId} title={p.name}>
                    <Avatar
                      src={p.avatarUrl || undefined}
                      sx={{
                        width: 28,
                        height: 28,
                        fontSize: 12,
                        bgcolor: p.color,
                        border: `2px solid ${p.color}`,
                      }}
                    >
                      {p.name.charAt(0).toUpperCase()}
                    </Avatar>
                  </Tooltip>
                ))}
              </AvatarGroup>
            )}
            <Tooltip title="Toggle comments">
              <IconButton
                onClick={() => setCommentsOpen((v) => !v)}
                color={commentsOpen ? "primary" : "default"}
              >
                <ChatBubbleOutline />
              </IconButton>
            </Tooltip>
            {downloadPdf && (
              <IconButton
                onClick={() => downloadPdf(content.id)}
                title="Download as PDF"
              >
                <PictureAsPdf />
              </IconButton>
            )}
            <IconButton
              onClick={() => copyContent(markdown)}
              title="Copy content"
            >
              <ContentCopy />
            </IconButton>
          </Box>
        </Box>
      ) : (
        <></>
      )}

      <DialogContent>
        {(isIdeated || isGenerating) && selectingHook ? (
          // Hook selection interface for ideated content or generating content
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Show info if content is generating */}
            {isGenerating && !content.errorMessage && (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: "info.50", borderColor: "info.main" }}>
                <Typography variant="subtitle1" color="info.dark" gutterBottom>
                  Content is Generating
                </Typography>
                <Typography variant="body2" color="text.primary">
                  If the content appears to be stuck, you can select a hook below to restart the generation process.
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  If the problem persists, please contact support.
                </Typography>
              </Paper>
            )}
            
            {/* Show error message if content generation failed */}
            {content.errorMessage && (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: "error.50", borderColor: "error.main" }}>
                <Typography variant="subtitle1" color="error" gutterBottom>
                  Content Generation Failed
                </Typography>
                <Typography variant="body2" color="error.dark">
                  {content.errorMessage}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  You can select the same hook to retry or choose a different one.
                </Typography>
              </Paper>
            )}

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Topic Summary
              </Typography>
              <Typography variant="body1">{content.topicSummary}</Typography>
            </Paper>

            <Box>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ display: "flex", alignItems: "center" }}
              >
                <Lightbulb color="primary" sx={{ mr: 1 }} fontSize="small" />
                Available Hook Options
              </Typography>
              <Typography variant="caption" color="text.secondary" paragraph>
                {content.errorMessage
                  ? "Select the same hook to retry generation or choose a different one."
                  : isGenerating
                  ? "Select a hook to restart the content generation process."
                  : "Select one of the hooks below to generate full content. The hook will be used as the starting point for your content."}
              </Typography>

              <List sx={{ mt: 1 }}>
                {content.hookOptions &&
                  content.hookOptions.map((hook: string, index: number) => (
                    <ListItem key={index} disablePadding sx={{ mb: 1 }}>
                      <Card
                        variant="outlined"
                        sx={{
                          width: "100%",
                          cursor: "pointer",
                          "&:hover": { boxShadow: 2 },
                          bgcolor:
                            selectedHook === hook
                              ? "primary.50"
                              : "background.paper",
                          borderColor:
                            selectedHook === hook ? "primary.main" : undefined,
                        }}
                        onClick={() => setSelectedHook(hook)}
                      >
                        <CardContent>
                          <Typography variant="h6">{hook}</Typography>
                        </CardContent>
                      </Card>
                    </ListItem>
                  ))}
              </List>
            </Box>
          </Box>
        ) : (
          // Combined edit and preview interface
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <TextField
              label="Title"
              fullWidth
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                latestRef.current.title = e.target.value;
                scheduleAutosave();
              }}
            />

            {content.images && content.images.length > 0 && (
              <ContentViewerImageSlider images={content.images} />
            )}

            <Box sx={{ display: "flex", flex: 1, minHeight: "70vh", gap: 0 }}>
              <Box sx={{ position: "relative", flex: 1, display: "flex" }}>
                <Box
                  ref={editorBoxRef}
                  sx={{
                    position: "relative",
                    flex: 1,
                    minHeight: "70vh",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    overflow: "auto",
                    p: 2,
                    "& .ProseMirror": {
                      minHeight: "100%",
                      outline: "none",
                    },
                    "& .ProseMirror p.is-editor-empty:first-of-type::before": {
                      content: "attr(data-placeholder)",
                      color: "text.disabled",
                      float: "left",
                      pointerEvents: "none",
                      height: 0,
                    },
                    "& .comment-mark": {
                      backgroundColor: "rgba(255, 212, 0, 0.35)",
                      borderBottom: "2px solid #f9a825",
                      cursor: "pointer",
                    },
                    "& .comment-mark-resolved": {
                      backgroundColor: "rgba(200, 200, 200, 0.25)",
                      borderBottom: "2px solid #9e9e9e",
                    },
                  }}
                >
                  <EditorContent editor={editor} />
                  <RemoteCursors
                    editor={editor}
                    presence={remotePresence}
                    tick={cursorTick}
                    containerRef={editorBoxRef}
                  />
                </Box>

                {/* Google Docs-style action strip in right margin */}
                {hasTextSelection && !pendingAnchor && editor?.view && (() => {
                  const container = editorBoxRef.current;
                  if (!container) return null;
                  const containerRect = container.getBoundingClientRect();
                  const sel = editor.state.selection;
                  let fromCoords, toCoords;
                  try {
                    fromCoords = editor.view.coordsAtPos(sel.from);
                    toCoords = editor.view.coordsAtPos(sel.to);
                  } catch (_) {
                    return null;
                  }
                  const midY = (fromCoords.top + toCoords.bottom) / 2;
                  const top = midY - containerRect.top + container.scrollTop - 24;
                  return (
                    <Paper
                      elevation={3}
                      sx={{
                        position: "absolute",
                        top: Math.max(0, top),
                        right: 8,
                        zIndex: 20,
                        borderRadius: 6,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        py: 0.5,
                        px: 0.25,
                      }}
                    >
                      <Tooltip title="Add comment" placement="right">
                        <IconButton
                          size="small"
                          onClick={startNewCommentFromSelection}
                          sx={{ color: "primary.main" }}
                        >
                          <AddCommentOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Suggest edit" placement="right">
                        <IconButton
                          size="small"
                          onClick={startNewCommentFromSelection}
                          sx={{ color: "primary.main" }}
                        >
                          <EditNoteOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Paper>
                  );
                })()}
              </Box>
              {commentsOpen && companyId && getToken && (
                <CommentsPanel
                  contentId={content.id}
                  companyId={companyId}
                  getToken={getToken as () => Promise<string>}
                  comments={comments}
                  setComments={setComments}
                  activeThreadId={activeThreadId}
                  setActiveThreadId={setActiveThreadId}
                  pendingAnchor={pendingAnchor}
                  clearPendingAnchor={() => setPendingAnchor(null)}
                  currentUserUid={currentUserUid || null}
                  showResolved={showResolvedComments}
                  setShowResolved={setShowResolvedComments}
                  editorContainerRef={editorBoxRef}
                  editor={editor}
                  onCommentsChanged={fetchComments}
                />
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Typography variant="body2" color="text.secondary">
            {countWords(markdown)} words
          </Typography>
          {!selectingHook && (
            <Typography
              variant="caption"
              color={saveState === "error" ? "error" : "text.secondary"}
            >
              {saveState === "saving" && "Saving…"}
              {saveState === "saved" && "All changes saved"}
              {saveState === "error" && "Save failed — will retry on next edit"}
            </Typography>
          )}
        </Box>

        <Box>
          <Button onClick={handleClose}>Cancel</Button>

          {(isIdeated || isGenerating) && selectingHook ? (
            <LoadingButton
              loading={generatingContent}
              onClick={() =>
                selectedHook && selectHookAndGenerate(selectedHook)
              }
              variant="contained"
              disabled={!selectedHook}
              color="primary"
              sx={{ ml: 1 }}
            >
              {content.errorMessage
                ? "Retry Generation"
                : isGenerating
                ? "Restart Generation"
                : "Generate Full Content"}
            </LoadingButton>
          ) : (
            <LoadingButton
              loading={loading}
              onClick={handleSubmit}
              variant="contained"
              disabled={!validForm()}
              sx={{ ml: 1 }}
            >
              Save
            </LoadingButton>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

const RemoteCursors: React.FC<{
  editor: any;
  presence: Array<{
    clientId: string;
    name: string;
    avatarUrl: string | null;
    color: string;
    cursor: { pos: number; end: number };
  }>;
  tick: number;
  containerRef: React.RefObject<HTMLDivElement>;
}> = ({ editor, presence, tick, containerRef }) => {
  const [, forceRender] = useState(0);
  useEffect(() => {
    forceRender((n) => n + 1);
  }, [tick, presence]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = () => forceRender((n) => n + 1);
    el.addEventListener("scroll", handler, { passive: true });
    window.addEventListener("resize", handler);
    return () => {
      el.removeEventListener("scroll", handler);
      window.removeEventListener("resize", handler);
    };
  }, [containerRef]);

  if (!editor || !editor.view) return null;
  const view = editor.view;
  const container = containerRef.current;
  if (!container) return null;
  const containerRect = container.getBoundingClientRect();

  return (
    <>
      {presence.map((p) => {
        const docSize = view.state.doc.content.size;
        const pos = Math.max(0, Math.min(p.cursor.pos, docSize));
        let coords;
        try {
          coords = view.coordsAtPos(pos);
        } catch (_) {
          return null;
        }
        const top = coords.top - containerRect.top + container.scrollTop;
        const left = coords.left - containerRect.left + container.scrollLeft;
        const height = coords.bottom - coords.top;
        return (
          <Box
            key={p.clientId}
            sx={{
              position: "absolute",
              top,
              left,
              height,
              width: "2px",
              backgroundColor: p.color,
              pointerEvents: "none",
              zIndex: 10,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -24,
                left: -2,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                padding: "2px 6px 2px 2px",
                borderRadius: "12px 12px 12px 0",
                backgroundColor: p.color,
                whiteSpace: "nowrap",
                pointerEvents: "none",
              }}
            >
              <Avatar
                src={p.avatarUrl || undefined}
                sx={{
                  width: 18,
                  height: 18,
                  fontSize: 10,
                  bgcolor: "white",
                  color: p.color,
                  fontWeight: 700,
                }}
              >
                {p.name.charAt(0).toUpperCase()}
              </Avatar>
              <Box
                sx={{
                  fontSize: 11,
                  lineHeight: 1,
                  color: "white",
                  fontWeight: 600,
                }}
              >
                {p.name}
              </Box>
            </Box>
          </Box>
        );
      })}
    </>
  );
};
